import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { saveUser } from '../../redux/actions';
import API from '../../utils/API';
import makeID from '../../utils/makeID';
import ImageDropperCropper from '../../pages/nonprofitProfile/ImageDropperCropper/ImageDropperCropper';
import UserPreferences from '../../components/navbar/donorModals/UserPreferences';
import { getOriginalFileURLFromCroppedURL } from '../../modules/file/file.utils';
import { IOnImageChangeEvent, IOnImageDeleteEvent } from '../../types/shared.types';
import MobileTopBar from '../../components/common/MobileTopBar';
import { FaArrowLeft } from 'react-icons/fa';

export interface IUserPreferences {
  ShareMyPrivateInfo: {
    name: boolean;
    gender: boolean;
    age: boolean;
    email: boolean;
    telephone: boolean;
    location: boolean;
  };
  demographic: { gender: string; age: number };
  address: { city: string; state: string };
  telephone: string;
  otherGender: string;
}

const DonorProfilePage: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const reduxUser = useSelector((state: any) => state.signin);

  const [formState, setFormState] = useState({
    firstName: reduxUser.firstName || '',
    lastName: reduxUser.lastName || '',
    organization_company: reduxUser.organization_company || '',
    profileImg: reduxUser.profileImg || '',
    _id: reduxUser._id
  });

  const [userPreferences, setUserPreferences] = useState<IUserPreferences | undefined>(undefined);
  const [UserValidations, setUserValidation] = useState(true);
  const [newImageUpload, setNewImageUpload] = useState<IOnImageChangeEvent | undefined>(undefined);
  const [imageDelete, setImageDelete] = useState<IOnImageDeleteEvent | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleChange = (e: any) => {
    setFormState({ ...formState, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (UserValidations === false) return;

    setIsLoading(true);
    try {
      let newFormState: any = { ...formState };

      if (userPreferences !== undefined) {
        newFormState = {
          ...newFormState,
          ShareMyPrivateInfo: userPreferences.ShareMyPrivateInfo,
          demographic: userPreferences.demographic,
          address: userPreferences.address,
          telephone: userPreferences.telephone
        };
      }

      // Upload new images (cropped + original) if changed
      if (newImageUpload !== undefined) {
        const uploadRes = await API.uploadFile(
          newImageUpload.croppedImage,
          newImageUpload.croppedImageFileName,
          formState.profileImg || undefined
        ).catch((err: any) => { console.error(err); return null; });

        // Stable S3 key (overwritten in place) => cache-bust so the browser refetches.
        if (uploadRes?.data?.data?.url) newFormState.profileImg = `${uploadRes.data.data.url}?v=${Date.now()}`;

        if (newImageUpload.originalImage && newImageUpload.originalImageFileName) {
          await API.uploadFile(
            newImageUpload.originalImage,
            newImageUpload.originalImageFileName,
            getOriginalFileURLFromCroppedURL(formState.profileImg) || undefined
          ).catch(console.error);
        }
      }

      // Delete image if requested
      if (imageDelete) {
        if (formState.profileImg) {
          await Promise.all([
            API.deleteFile(formState.profileImg).catch(console.error),
            API.deleteFile(getOriginalFileURLFromCroppedURL(formState.profileImg)).catch(console.error),
          ]);
        }
        newFormState.profileImg = '';
      }

      const res: any = await API.updateUser(newFormState).catch((err: any) => console.log(err));

      if (res?.status === 200) {
        dispatch(
          saveUser({
            ...reduxUser,
            ...formState,
            profileImg: newFormState.profileImg
          })
        );

        setMessage({ type: 'success', text: 'Profile updated successfully!' });
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setMessage({ type: 'error', text: 'Error updating profile. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUserPreferences = (updatedPreferences: IUserPreferences) => {
    setUserPreferences({ ...updatedPreferences });
    setFormState({ ...formState });
  };

  const handleImageChange = async (event: IOnImageChangeEvent) => {
    setNewImageUpload(event);
    setImageDelete(undefined);
  };

  const handleImageDelete = async (event: IOnImageDeleteEvent) => {
    setImageDelete(event);
    setNewImageUpload(undefined);
  };

  const fetchPreferences = () => {
    API.fetchUserPreferences(reduxUser._id)
      .then((response: any) => {
        if (!response) return;
        const newPreferences: IUserPreferences = {
          ShareMyPrivateInfo: response.data.ShareMyPrivateInfo,
          demographic: response.data.demographic,
          address: response.data.address,
          telephone: response.data.telephone,
          otherGender:
            response.data.demographic.gender === 'male' ||
            response.data.demographic.gender === 'female' ||
            response.data.demographic.gender === 'non-binary' ||
            response.data.demographic.gender === 'other'
              ? ''
              : response.data.demographic.gender
        };
        setUserPreferences(newPreferences);
        setFormState({ ...formState });
      })
      .catch(console.log);
  };

  useEffect(() => {
    if (reduxUser?._id) {
      fetchPreferences();
    }
  }, [reduxUser]);

  // Auto-close message after 2 seconds
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage(null);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  return (
    <>
      <MobileTopBar />

      <div>
        <div
          style={{
            maxWidth: '800px',
            margin: '0 auto',
            backgroundColor: 'white',
            overflow: 'hidden'
          }}
        >
          <FaArrowLeft style={{ marginLeft: '2rem', marginTop: '1rem', cursor: 'pointer' }} onClick={() => history.goBack()} />

          {/* Header */}
          <div
            style={{
              padding: '0px 32px 20px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <h2
              style={{
                margin: 0,
                fontSize: '24px',
                fontWeight: 600,
                color: '#111827'
              }}
            >
              Edit my profile
            </h2>
          </div>

          {/* Body */}
          <div style={{ padding: '0 32px' }}>
            <div className="profile-content">
              {/* ======= Top Block — matches screenshot ======= */}
              <div style={{ marginBottom: '24px' }}>
                <label
                  style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: 600,
                    marginBottom: '12px',
                    color: '#374151'
                  }}
                >
                  Profile image
                </label>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '112px 1fr',
                    alignItems: 'start'
                  }}
                >
                  {/* Left: Avatar */}
                  <div className="profile-image-container with-pencil" style={{ width: 160 }}>
                    <ImageDropperCropper
                      defaultThumbNail={reduxUser.profileImg}
                      field={'profileImg'}
                      aspect="squareCrop"
                      fileName={`prof-pic-${reduxUser._id + makeID(15)}`}
                      onImageChange={handleImageChange}
                      onImageDelete={handleImageDelete}
                    />
                  </div>

                  {/* Right: Names + Company fields */}
                  <div>
                    {/* Row: First + Last name */}
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '12px',
                        marginBottom: '12px'
                      }}
                    >
                      <div>
                        <input
                          value={formState.firstName}
                          name="firstName"
                          type="text"
                          placeholder="Name"
                          onChange={handleChange}
                          style={{
                            width: '100%',
                            padding: '6px 14px',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            fontSize: '14px'
                          }}
                        />
                      </div>
                      <div>
                        <input
                          value={formState.lastName}
                          name="lastName"
                          type="text"
                          placeholder="Last Name"
                          onChange={handleChange}
                          style={{
                            width: '100%',
                            padding: '6px 14px',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            fontSize: '14px'
                          }}
                        />
                      </div>
                    </div>

                    {/* Company / Organization (full width) */}
                    <div>
                      <input
                        value={formState.organization_company}
                        name="organization_company"
                        type="text"
                        placeholder="Company / Organization"
                        onChange={handleChange}
                        style={{
                          width: '100%',
                          padding: '6px 14px',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
              {/* ======= End Top Block ======= */}

              {/* Preferences Section */}
              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: '32px' }}>
                  <div
                    style={{
                      marginBottom: '16px',
                      padding: '16px',
                      backgroundColor: '#f8fafc',
                      borderRadius: '8px'
                    }}
                  >
                    <h4
                      style={{
                        margin: '0 0 8px 0',
                        fontSize: '16px',
                        fontWeight: 600,
                        color: '#111827'
                      }}
                    >
                      Preferences
                    </h4>
                    <p
                      style={{
                        margin: 0,
                        fontSize: '14px',
                        color: '#6b7280',
                        lineHeight: '1.4'
                      }}
                    >
                      When you donate the information you check below will be shared with the nonprofits in order to improve their operations.
                    </p>
                  </div>

                  {!userPreferences ? (
                    <div
                      style={{
                        textAlign: 'center',
                        padding: '36px',
                        color: '#6b7280',
                        border: '1px dashed #e5e7eb',
                        borderRadius: '12px'
                      }}
                    >
                      LOADING PREFERENCES...
                    </div>
                  ) : (
                    <UserPreferences
                      preferences={userPreferences}
                      setUserPreferences={handleUserPreferences}
                      userName={`${reduxUser.firstName} ${reduxUser.lastName}`}
                      email={reduxUser.email}
                      setUserValidation={setUserValidation}
                    />
                  )}
                </div>

                {/* Footer Actions */}
                <div
                  style={{
                    display: 'flex',
                    gap: '12px',
                    justifyContent: 'center',
                    paddingTop: '20px',
                    borderTop: '1px solid #e5e7eb'
                  }}
                >
                  <button
                    type="submit"
                    disabled={!UserValidations || isLoading}
                    style={{
                      padding: '5px 24px',
                      borderRadius: '14px',
                      fontSize: '14px',
                      fontWeight: 500,
                      cursor: UserValidations === false || isLoading ? 'not-allowed' : 'pointer',
                      backgroundColor: UserValidations === false || isLoading ? '#9ca3af' : '#5271ff',
                      color: 'white',
                      border: '1px solid',
                      borderColor: UserValidations === false || isLoading ? '#9ca3af' : '#5271ff',
                      transition: 'all 0.2s',
                      opacity: isLoading ? 0.5 : 1,
                      minWidth: '100px'
                    }}
                  >
                    {isLoading ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    type="button"
                    onClick={() => history.goBack()}
                    disabled={isLoading}
                    style={{
                      padding: '5px 24px',
                      borderRadius: '14px',
                      fontSize: '14px',
                      fontWeight: 500,
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      backgroundColor: '#5271ff',
                      color: 'white',
                      border: '1px solid #5271ff',
                      transition: 'all 0.2s',
                      opacity: isLoading ? 0.5 : 1,
                      minWidth: '100px'
                    }}
                  >
                    Close
                  </button>
                  
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Message Toast */}
        {message && (
          <div
            style={{
              position: 'fixed',
              top: '20px',
              right: '20px',
              padding: '16px 24px',
              borderRadius: '8px',
              color: 'white',
              fontWeight: 500,
              fontSize: '14px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              zIndex: 1001,
              backgroundColor: message.type === 'success' ? '#10b981' : '#ef4444',
              animation: 'slideInRight 0.3s ease-out'
            }}
          >
            <span>{message.text}</span>
          </div>
        )}

        {/* Bottom padding for fixed footer */}
        <div style={{ height: '80px' }}></div>
      </div>

      <style jsx>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </>
  );
};

export default DonorProfilePage;
